import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '~components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~components/ui/sheet';
import { APP_NAME } from '~config';
import { changelogReleases } from '../data';
import { getLatestRelease } from '../utils';
import { ReleaseNotes } from './ReleaseNotes';

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Displays the latest production release and links to the complete changelog.
 */
const ChangelogSheet = () => {
  const latestRelease = getLatestRelease(changelogReleases);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="font-semibold text-primary text-xs"
          aria-label={
            latestRelease ? `Open release notes for version ${latestRelease.version}` : 'Open latest release notes'
          }
          title={latestRelease ? `Release ${latestRelease.version}` : 'Open latest release notes'}
        >
          {latestRelease ? `v${latestRelease.version}` : 'Release notes'}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-xl" showOverlay={false}>
        <SheetHeader>
          <div className="flex flex-col gap-1">
            <SheetTitle>Latest release notes</SheetTitle>
            <SheetDescription>Recent production changes for {APP_NAME}.</SheetDescription>
          </div>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-4">
          {latestRelease ? (
            <>
              <ReleaseNotes release={latestRelease} />
              <SheetClose asChild>
                <Link
                  to="/changelog"
                  onClick={() => setIsOpen(false)}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  View full release notes
                </Link>
              </SheetClose>
            </>
          ) : (
            <p role="status">No release notes are available.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { ChangelogSheet };
